import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import makeStyles from '@mui/styles/makeStyles';
import {
  Button, Box, FormControl, Grid, InputLabel, LinearProgress, FormGroup, Link,
  Typography, TextField, Select, MenuItem, Checkbox, FormControlLabel,
} from '@mui/material';
import Api from './Api';
import UIClasses from './UIClasses';

const useStyles = makeStyles((theme) => ({
  root: {
    margin: '1rem 2rem 2rem 2rem',
    display: 'flex',
    justifyContent: 'center',
    textAlign: 'center'
  },
  grid: {
    marginTop: '1rem',
    gap: '.8rem'
  },
  description: {
    paddingBottom: '20px',
    textTransform: 'none'
  },
  linearProgress: {
    marginTop: '1.5rem'
  },
  form: {
    display: 'flex',
    justifyContent: 'center',
    textAlign: 'left'
    //textAlign: 'center',
    //width: 'min-content'
  },
  title: UIClasses.title,
  text: UIClasses.text,
  link: UIClasses.link,
  //textfield: UIClasses.textfield,
  input: UIClasses.input,
}));

export default () => {
    const classes = useStyles();
    const location = useLocation();
    const [ showPhone, setShowPhone ] = useState(false);
    const [ demoOnly, setDemoOnly ] = useState(false);
    const [ loading, setLoading ] = useState(false);
    const [ countries, setCountries ] = useState('');
    const [ selectedCountry, setSelectedCountry ] = useState('');
    const { getCountries, submitRegistration, showStatus } = Api();

    useEffect(() => {
      setShowPhone(location.pathname.startsWith('/register-osff'));
      setDemoOnly(location.pathname === '/register-osff-demo');
      getCountries().then((countries) => setCountries(countries));
    }, []);

    const setCountryChanged = (event) => setSelectedCountry(event.target.value);

    const submitRegister = (event) => {
      event.preventDefault();
      let values = Object.fromEntries(new FormData(event.target).entries());
      values = {
        ...values,
        country: selectedCountry,
        password: values.password?.trim(),
        password2: values.password2?.trim(),
        newsletter: values.newsletter === 'on',
        demoOnly,
      };
      delete values.policy;

      if (values.password !== values.password2) {
        showStatus('error', 'Your passwords do not match');
      } else if (values.password.length < 8) {
        showStatus('error', 'Your password needs to be at least 8 characters long');
      } else if (values.password.length > 100) {
        showStatus('error', 'Your password needs to be at most 100 characters long');
      } else if (!values.password.match(/[A-Z]/g)) {
        showStatus('error', 'Your password needs to have at least 1 uppercase character');
      } else if (!values.password.match(/[a-z]/g)) {
        showStatus('error', 'Your password needs to have at least 1 lowercase character');
      } else if (!values.password.match(/\d/g)) {
        showStatus('error', 'Your password needs to have at least 1 number');
      } else if (!values.password.match(/\W/g)) {
        showStatus('error', 'Your password needs to have at least 1 symbol');
      } else {
        setLoading(true);
        if (values.phone?.trim() === '') {
          delete values.phone;
        }
        submitRegistration(values).then(() => {
          setLoading(false);
          document.querySelector('#registration-form').reset();
          setSelectedCountry('');
          showStatus('success', 'Registration successful. Please check your email for instructions.');
        }).catch((error) => {
          setLoading(false);
          showStatus('error', error.message);
        });
      }
    };

    const PolicyLabel = (<>
      I agree with Symphony's {` `}
      <Link color="inherit" href="//symphony.com/privacy-policy/" target="_blank" rel="noopener">
        privacy policy &amp; terms
      </Link>
    </>);

    const TopAlignedCheckbox = ({ name }) => (
      <div style={{ marginTop: '-1.2rem' }}>
        <Checkbox name={name} defaultChecked />
      </div>
    );

    return (
      <div className={classes.root}>
        <div>
          <Typography className={`${classes.title}`}>Registration</Typography>
          <Typography className={`${classes.text} ${classes.description}`}>Enter your personal information to register to the Symphony Developer Center:</Typography>
          <form className={classes.form} id="registration-form" onSubmit={submitRegister} autoComplete="off">
            <Grid container item direction="column" className={classes.grid} xs={12} sm={8} lg={5}>
              <TextField name="email" label="Email" variant="outlined" required type="email" className={classes.textfield} inputProps={{ className: classes.input }} />
              { showPhone && <TextField name="phone" label="Mobile Number (+1 123 123 123)" variant="outlined" type="tel" className={classes.textfield} inputProps={{ className: classes.input }} /> }
              <TextField name="password" label="Password" variant="outlined" required type="password" className={classes.textfield} inputProps={{ className: classes.input }} />
              <TextField name="password2" label="Repeat Password" variant="outlined" required type="password" className={classes.textfield} inputProps={{ className: classes.input }} />
              <TextField name="firstName" label="First Name" variant="outlined" required className={classes.textfield} inputProps={{ className: classes.input }} />
              <TextField name="lastName" label="Last Name" variant="outlined" required className={classes.textfield} inputProps={{ className: classes.input }} />
              <FormControl name="country" variant="outlined" className={classes.textfield} required>
                <InputLabel id="country-label">Country</InputLabel>
                <Select labelId="country-label" label="Country" value={selectedCountry} onChange={setCountryChanged}>
                  {
                    countries && countries.map((country) => (
                      <MenuItem key={country.code} value={country.code.toUpperCase()}>{country.name}</MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
              <TextField name="company" label="Company" variant="outlined" className={classes.textfield} inputProps={{ className: classes.input }} />
              <FormGroup sx={{ flexFlow: 'wrap' }}>
                <FormControlLabel control={<Checkbox defaultChecked name="policy" required />} label={PolicyLabel} />
                <FormControlLabel
                  control={<TopAlignedCheckbox name="newsletter" />}
                  label={`I agree to my email ${showPhone ? 'and mobile number ' : ''}being stored and used to receive communications from Symphony Developer Relations`}
                />
              </FormGroup>
              <Box height='3rem'>
              {
                loading ? <LinearProgress className={classes.linearProgress} /> : (
                  <Button variant="blue" type="submit" disabled={loading}>
                    Register
                  </Button>
                )
              }
              </Box>
            </Grid>
          </form>
        </div>
      </div>
    )
};
